import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * Stripe-style facts grid: tight label/value rows. This is how record details
 * are displayed everywhere — never a grid of labeled boxes.
 */
export function DefList({ className, children }: { className?: string; children: ReactNode }) {
  // `@container` lets each DefRow decide label-beside-value vs stacked based on
  // the list's own width, not the viewport — so record details stay readable in
  // a narrow side panel (e.g. the call panel) instead of shattering the value.
  return <dl className={cn('@container grid content-start gap-y-0.5', className)}>{children}</dl>;
}

interface DefRowProps {
  label: ReactNode;
  children?: ReactNode;
  /** Numeric/identifier values — renders with aligned (tabular) numerals. */
  mono?: boolean;
  className?: string;
}

export function DefRow({ label, children, mono, className }: DefRowProps) {
  const empty = children === null || children === undefined || children === '';
  return (
    <div
      className={cn(
        // Stack label over value when the list is narrow; switch to the
        // label|value two-column grid once there's room for both (@min-[18rem]).
        'grid grid-cols-1 items-baseline gap-x-4 gap-y-0.5 py-1 @min-[18rem]:grid-cols-[9.5rem_1fr]',
        className
      )}
    >
      <dt className="truncate text-sm text-zinc-400">{label}</dt>
      <dd
        className={cn(
          'min-w-0 text-sm break-words text-zinc-100',
          mono && 'tabular-nums',
          empty && 'text-zinc-600'
        )}
      >
        {empty ? '—' : children}
      </dd>
    </div>
  );
}
